// @flow

import React, { type Node, useCallback, useEffect, useRef } from 'react';
import { useEnrollment } from '../context';
import { useBankList } from '../hooks/use-bank-list';
import { useEnrollmentInitiation } from '../hooks/use-enrollment-initiation';
import { useEnrollmentPrefill } from '../hooks/use-enrollment-prefill';
import { CpfInput } from '../components/cpf-input';
import { BankSelector } from '../components/bank-selector';
import { isCpfValid } from '../utils/cpf-validation';
import {
  Container,
  Title,
  Subtitle,
  Form,
  InfoBanner,
  InfoIcon,
  InfoText,
  SubmitButton,
  TermsText,
  TermsLink,
} from './bank-linking-screen.styles';

export function BankLinkingScreen(): Node {
  const { data, dispatch } = useEnrollment();
  const { banks, loading: banksLoading, error: banksError } = useBankList();
  const initiateEnrollment = useEnrollmentInitiation();
  const { bankId: prefillBankId, identification: prefillIdentification, loading: prefillLoading } = useEnrollmentPrefill();
  const cpfPrefillAppliedRef = useRef<boolean>(false);
  const bankPrefillAppliedRef = useRef<boolean>(false);
  const isInitiating = data.state === 'INITIATING';

  useEffect(() => {
    if (prefillLoading) return;

    if (prefillIdentification && !cpfPrefillAppliedRef.current && !data.cpf) {
      cpfPrefillAppliedRef.current = true;
      dispatch({ type: 'SET_CPF', cpf: prefillIdentification });
    }

    if (prefillBankId && !bankPrefillAppliedRef.current && !data.bankId && !banksLoading && banks.length > 0) {
      const matchedBank = banks.find(bank => bank.code === prefillBankId);
      if (matchedBank) {
        bankPrefillAppliedRef.current = true;
        dispatch({
          type: 'SET_BANK',
          bankId: matchedBank.code,
          bankName: matchedBank.name,
          bankLogoUrl: matchedBank.logo_url || '',
        });
      }
    }
  }, [prefillLoading, prefillBankId, prefillIdentification, banksLoading, banks, data.cpf, data.bankId, dispatch]);

  const handleCpfChange = useCallback((rawDigits: string) => {
    dispatch({ type: 'SET_CPF', cpf: rawDigits });
  }, [dispatch]);

  const handleBankSelect = useCallback((bankId: string, bankName: string, bankLogoUrl: string) => {
    dispatch({ type: 'SET_BANK', bankId, bankName, bankLogoUrl });
  }, [dispatch]);

  const isCpfPrefilled = cpfPrefillAppliedRef.current && !!prefillIdentification;
  const isBankPrefilled = bankPrefillAppliedRef.current && !!prefillBankId;

  const cpfDigits = data.cpf.replace(/\D/g, '');
  const isFormValid = isCpfValid(cpfDigits) && data.bankId !== '';
  const canSubmit = isFormValid && !isInitiating;

  const handleSubmit = useCallback((event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canSubmit) {
      initiateEnrollment();
    }
  }, [canSubmit, initiateEnrollment]);

  return (
    <Container>
      <Title>Vincule sua conta bancária</Title>
      <Subtitle>Escolha sua instituição e informe seu CPF para continuar</Subtitle>

      <Form onSubmit={handleSubmit}>
        <CpfInput value={data.cpf} onChange={handleCpfChange} disabled={isCpfPrefilled} />
        <BankSelector
          banks={banks}
          selectedBankId={data.bankId}
          onSelect={handleBankSelect}
          loading={banksLoading}
          error={banksError}
          disabled={isBankPrefilled}
        />

        <InfoBanner>
          <InfoIcon>ℹ</InfoIcon>
          <InfoText>
            Você será redirecionado para a instituição selecionada para confirmar a operação e voltará automaticamente.
          </InfoText>
        </InfoBanner>

        <SubmitButton type="submit" disabled={!canSubmit}>
          {isInitiating ? 'Vinculando...' : 'Vincular conta'}
        </SubmitButton>

        <TermsText>
          Ao continuar você aceita os <TermsLink href="#">Termos e condições</TermsLink>
        </TermsText>
      </Form>
    </Container>
  );
}
